import React from 'react';
import {
  Toolbar,
  ToolbarItem,
  ToolbarSpacer
} from '@progress/kendo-react-buttons';
import { Component } from '../../../../dispatcher/modules';
import PuzzleButton from '../../../UI/button/PuzzleButton';
import _ from 'lodash';
import classNames from 'classnames';
import Configuration from '../../../../dispatcher/config/Configuration';
import { matchPath } from 'react-router-dom';
import { Position } from '../../../../dispatcher/Types';
import {
  SIDEBAR_PROPS,
  SIDEBAR_STATE,
  SIDEBAR_ROUTE_CONFIG,
  SIDEBAR_ACTION_CONFIG, 
  SIDEBAR_ROUTE_GROUP_SUBITEM
} from './Types';

const SIDEBAR_WIDTH = {
  collapsed: '48px',
  collapsedWithGroup: '64px',
  expanded: '240px'
};

/**
 * Sidebar class is Viewport component for navigation
 * @class Sidebar
 */
class Sidebar extends Component<SIDEBAR_PROPS, SIDEBAR_STATE> {
  /**
   * Custom action default configuration
   * @private
   */
  private addActionConfig: any = {
    text: this.translate('customActionText'),
    icon: 'add',
    action: null,
    path: null
  };

  state = {
    isSidebarCollapsed: true,
    extendedGroup: ''
  }

  constructor(props: any) {
    super(props);

    this.state = {
      ...this.state,
      ...props
    };

    _.isFunction(props.afterItemClick) && (this.afterItemClick = props.afterItemClick);
  }

  moduleDidMount() {
    this.setDocumentElementSidebarWidth();
  }

  /**
   * Will be triggered after item click handler
   * This is a dummy method, and it should be overridden via props if needed
   * @param {React.MouseEvent} event
   * @param {string} pathname
   * @param {string} path
   */
  afterItemClick = (event: React.MouseEvent, pathname?: string, path?: string) => {};

  /**
   * Setter for sidebar width on document element
   */
  setDocumentElementSidebarWidth = () => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      this.getSidebarWidth()
    );
  }

  /**
   * Returns `true` if Sidebar item is active/clicked
   * @param {(string|string[])} path Route path which will be rendered if item is active
   * @returns {boolean}
   */
  isItemActive = (path: string | string[] = null) => {
    const pathname = Configuration.getPathname();
    if (!path) return false;

    return _.isArray(path) ?
      path.some(path => matchPath(pathname, path)) :
      matchPath(pathname, path);
  }

  /**
   * Item click handler. Switches routes and adds after click function
   * @param {React.MouseEvent} event Click event
   * @param {string} path The path to be redirected to
   * @param {string} action Action name
   */
  handleItemClick = (event: React.MouseEvent, path?: string, action?: string) => {
    const pathname = document.location.pathname;
    this.afterItemClick(event, pathname, path);

    this.dispatch(action ? action : 'reloadPath', Position.end, { path });
  }

  /**
   * Renderer for item separator
   */
  renderItemSeparator = () => <div className={'tp-sidebar-separator'} />;

  /**
   * Renderer for group item
   * @param {SIDEBAR_ROUTE_CONFIG} item
   * @param {number} index
   * @param {boolean} isGroupExpanded
   * @param {boolean} isSidebarCollapsed
   */
  renderRouteGroupItem = (item: SIDEBAR_ROUTE_CONFIG, index: number, isGroupExpanded: boolean) => (
    <ToolbarItem
      className={classNames(
        'tp-sidebar-toolbar-group d-flex k-align-item-center',
        {
          'k-state-disabled': item.folderDisabled
        }
      )}
      key={`main-sidebar-link-${index}`}
    >
      <PuzzleButton
        key={item.path}
        aria-label={item.text}
        className={'tp-sidebar-toolbar-item-button'}
        tooltipPosition={'right'}
        withTooltip
        titleText={item.text}
        style={{ width: '100%' }}
        onClick={() => {
          this.setState({
            extendedGroup: isGroupExpanded ? '' : item.path
          });
        }}
      >
        <i
          className={'tp-icon tp-arrow-chevron-right tp-sidebar-group-icon'}
          style={{ transform: `rotate(${isGroupExpanded ? 90 : 0}deg)` }}
        />
        <i
          className={`tp-icon tp-${item.icon} tp-sidebar-toolbar-item-link-icon`}
          aria-hidden="true"
        />
        {this.getIsSidebarCollapsed() ? undefined : <span>{item.text}</span>}
      </PuzzleButton>
    </ToolbarItem>
  )

  /**
   * Renderer for group sub item
   * @param {SIDEBAR_ROUTE_GROUP_SUBITEM} subItem
   * @param {number} index
   * @param {number} subItemIndex
   * @param isLastItem boolean
   * @param path string
   */
  renderRouteGroupSubItem = (subItem: SIDEBAR_ROUTE_GROUP_SUBITEM, index: number, subItemIndex: number, isLastItem: boolean, path: string) => {
    const isSidebarCollapsed = this.getIsSidebarCollapsed();
    return (
      <ToolbarItem
        className={classNames(
          'k-align-item-center tp-sidebar-toolbar-group-subitem',
          {
            'k-state-disabled': subItem.folderDisabled,
            'tp-sidebar-toolbar-subitem-collapsed': isSidebarCollapsed,
            'tp-sidebar-toolbar-subitem-expanded': !isSidebarCollapsed
          }
        )}
        key={`main-sidebar-link-${index}-${subItemIndex}`}
      >
        <PuzzleButton
          key={subItem.path}
          children={isSidebarCollapsed ? undefined : <span>{subItem.text}</span>}
          aria-label={subItem.text}
          aria-pressed={!!this.isItemActive(subItem.path)}
          className={classNames(
            'tp-sidebar-toolbar-item-button d-flex',
            {
              'tp-active': this.isItemActive(subItem.path),
              'tp-last-nav-item': isLastItem
            }
          )}
          tooltipPosition={'right'}
          withTooltip
          titleText={subItem.text}
          iconClass={`tp-icon tp-${subItem.icon} tp-sidebar-toolbar-item-link-icon`}
          onClick={event => this.handleItemClick(event, path, subItem.action)}
        />
      </ToolbarItem>
    );
  }

  /**
   * Renderer for simple action on sidebar
   * @param item
   * @param index
   * @param path
   */
  renderSimpleRouteItem = (item: SIDEBAR_ROUTE_CONFIG, index: number, path: string) => (
    <ToolbarItem
      className={classNames(
        'd-flex k-align-item-center tp-sidebar-toolbar-item tp-sidebar-toolbar-item-simple tp-100 k-justify-content-center',
        {
          'k-state-disabled': item.folderDisabled
        }
      )}
      key={`main-sidebar-link-${index}`}
    >
      <PuzzleButton
        aria-label={item.text}
        aria-pressed={!!this.isItemActive(item.path)}
        className={classNames(
          'tp-sidebar-toolbar-item-button',
          {
            'tp-active': this.isItemActive(item.path)
          }
        )}
        tooltipPosition={'right'}
        withTooltip
        titleText={item.text}
        iconClass={`tp-icon tp-${item.icon} tp-sidebar-toolbar-item-link-icon`}
        onClick={event => this.handleItemClick(event, path, item.action)}
      />
    </ToolbarItem>
  );

  /**
   * Getter for sidebar is colapsed
   */
  getIsSidebarCollapsed = () => this.state.isSidebarCollapsed;

  /**
   * Renderer for action item
   * @param item
   * @param index
   */
  renderActionItem = (item: SIDEBAR_ACTION_CONFIG, index: number) => {
    if (item.hidden) {
      return null;
    }

    return (
      <ToolbarItem
        className={classNames(
          'd-flex k-align-item-center',
          {
            'k-state-disabled': item.folderDisabled
          }
        )}
        key={`main-sidebar-link-${index}`}
      >
        {item.component}
      </ToolbarItem>
    );
  }

  /**
   * Returns single toolbar item
   * @param {Paths} item Toolbar item configuration
   * @param {number} index
   */
  renderRouteItem = (item: SIDEBAR_ROUTE_CONFIG, index: number) => {
    if (item.hidden) {
      return null;
    }

    const { extendedGroup } = this.state;
    const path = _.isArray(item.path) ? item.path[0] : item.path;

    let toolbarItems = [];

    if (item.isGroup) {
      const isGroupExpanded = extendedGroup === item.path;
      toolbarItems.push(this.renderRouteGroupItem(item, index, isGroupExpanded));
      if (isGroupExpanded) {
        item.items.forEach((subItem, subItemIndex) => {
          const isLastItem = subItemIndex + 1 === item.items.length;
          toolbarItems.push(this.renderRouteGroupSubItem(subItem, index, subItemIndex, isLastItem, path));
        });
      }
      toolbarItems.push(this.renderItemSeparator());
      return toolbarItems;
    } else if (item.path || item.action) {
      return this.renderSimpleRouteItem(item, index, path);
    }
    return null;
  }

  /**
   * Updates custom action
   * @param {string} customAction
   */
  updateCustomAction = (customAction) => {
    this.setState({
      customAction
    });
  }

  /**
   * Toggle sidebar handler
   */
  toggleSidebar = () => {
    this.setState({
      isSidebarCollapsed: !this.getIsSidebarCollapsed()
    });
    this.setDocumentElementSidebarWidth();
  }

  /**
   * getter for flag if sidebar have grouped routes
   */
  isSidebarHaveGroups = () => this.props?.routesConfig?.some(route => route.isGroup);

  /**
   * Getter for current sidebar width
   */
  getSidebarWidth = (): string => this.getIsSidebarCollapsed() ?
    (this.isSidebarHaveGroups() ? SIDEBAR_WIDTH.collapsedWithGroup : SIDEBAR_WIDTH.collapsed)
    : SIDEBAR_WIDTH.expanded

  /**
   * @override
   */
  renderBody() {
    const { showAddAction = false, isExpandable = false } = this.props;
    const { routesConfig = [], actionsConfig = [], customAction } = this.props;

    let customActionConfig = {
      ...this.addActionConfig,
      ...customAction
    };

    return (
      <nav
        className="tp-viewport-sidebar"
        style={{
          width: this.getSidebarWidth(),
          transitionDuration: '500ms'
        }}
      >
        <div className="tp-sidebar">
          <Toolbar className={'tp-sidebar-toolbar'}>
            {showAddAction ? <div
              className="tp-sidebar-custom-action"
            > {this.renderRouteItem(customActionConfig, -1)} </div> : null}

            {/* render sidebar route items */}
            {routesConfig.map(this.renderRouteItem)}

            <ToolbarSpacer /> {/* render sidebar spacer */}

            {/* render sidebar action items */}
            {actionsConfig.map(this.renderActionItem)}
            {isExpandable ? <i
              className="tp-icon tp-arrow-chevron-right tp-sidebar-expand-icon"
              style={{ 
                transform: `rotate(${this.getIsSidebarCollapsed() ? 0 : -180}deg)`,
                transition: '500ms'
              }}
              onClick={this.toggleSidebar}
            /> : null}
          </Toolbar>
        </div>
      </nav>
    );
  }
}

export default Sidebar;
